import { NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store, select } from '@ngrx/store';
import {
  UppyAngularDashboardModalModule,
  UppyAngularDashboardModule,
  UppyAngularDragDropModule,
  UppyAngularProgressBarModule,
  UppyAngularStatusBarModule,
} from '@uppy/angular';
import Uppy from '@uppy/core';
import { Subscription } from 'rxjs';
import { Video } from '../../../../models/video.model';
import * as videoActions from '../../../../state/actions/video.actions';
import { AppState } from '../../../../state/appstate';
import * as videoSelector from '../../../../state/selectors/video.selector';
import { LoadingDotsComponent } from '../../../components/spinners/loading-dots/loading-dots.component';

@Component({
  selector: 'app-edit-video',
  standalone: true,
  templateUrl: './edit-video.component.html',
  imports: [
    ReactiveFormsModule,
    NgIf,
    NgFor,
    RouterLink,
    LoadingDotsComponent,
    UppyAngularDashboardModalModule,
    UppyAngularDashboardModule,
    UppyAngularDragDropModule,
    UppyAngularProgressBarModule,
    UppyAngularStatusBarModule,
  ],
})
export class EditVideoComponent implements OnDestroy {
  updateForm: FormGroup;

  tags: Array<string> = [];

  id: string | null = null;

  video: Video | undefined;

  videoStateSub: Subscription | undefined;
  working: boolean = false;

  uppy: Uppy = new Uppy({
    restrictions: {
      // we can add restrictions here:
      maxFileSize: 5242880, // 5 MB
      maxNumberOfFiles: 1,
      minNumberOfFiles: 1,
      allowedFileTypes: ['.jpg', '.jpeg', '.png'],
    },
  });

  constructor(private store: Store<AppState>, private route: ActivatedRoute) {
    this.id = this.route.snapshot.paramMap.get('id');

    this.updateForm = new FormGroup({
      title: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(150),
      ]),
      description: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(500),
      ]),
      tagField: new FormControl(''),
    });
    this.store
      .pipe(select(videoSelector.getItemById(this.id!)))
      .subscribe((video) => {
        if (video != null) {
          this.video = video;
          this.updateForm.patchValue({
            title: video.title,
            description: video.description,
            tagField: '',
          });
          this.tags = video.tags ?? [];
        }
      });
    this.videoStateSub = this.store
      .select((state) => state.videos)
      .subscribe((videoState) => {
        this.working = videoState.inProgress;
      });
  }

  ngOnDestroy(): void {
    this.videoStateSub?.unsubscribe();
  }

  addTag() {
    this.updateForm.get('tagField')?.setErrors(null);
    if (this.updateForm.get('tagField')?.value === null) return;
    const fieldValue: string = this.updateForm.get('tagField')?.value.trim();
    if (fieldValue === '') return;
    if (fieldValue.includes(' '))
      return this.updateForm.controls['tagField'].setErrors({
        invalidValue: true,
      });
    if ((this.tags.length ?? 0) >= 10)
      return this.updateForm.controls['tagField'].setErrors({
        maxValuesAllowed: true,
      });
    this.tags = [...this.tags, fieldValue];
    this.updateForm.get('tagField')?.setValue('');
    this.updateForm.get('tagField')?.markAsPristine();
    // console.log(this.tags);
  }

  removeTag(tag: number) {
    this.tags.splice(tag, 1);
  }

  update() {
    this.store.dispatch(
      videoActions.update({
        video: {
          id: this.id!,
          title: this.updateForm.get('title')?.value,
          description: this.updateForm.get('description')?.value,
          tags: this.tags,
        },
      })
    );
  }
}
